// src/appPage/AuthSystem.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LoginView from "../components/shared/auth/LoginView";
import SignupView from "../components/shared/auth/SignupView";
import ForgotPasswordView from "../components/shared/auth/ForgotPasswordView";
import ResetPasswordView from "../components/shared/auth/ResetPasswordView";

import PendingView from "../components/customer/PendingView";
import ProductsDashboard from "../components/customer/ProductsDashboard";

import ControlPanelView from "../components/admin/ControlPanelView";
import CompanyDetailsModal from "../components/admin/CompanyDetailsModal";
import ProductManagementModal from "../components/admin/ProductManagementModal";
import ProductSelectionModal from "../components/customer/ProductSelectionModal";

// ✅ Sales Layout (Sidebar + Outlet)
import SalesLayout from "../components/sales/SalesLayout";

// Sales pages
import ClientsPage from "../components/sales/pages/clients/ClientsPage";
import ClientInteractionsPage from "../components/sales/pages/client-interactions/ClientInteractionsPage";
import SalesNotesPage from "../components/sales/pages/salesNotes/SalesNotesPage";
import SalesTemplatesPage from "../components/sales/pages/sales-templates/SalesTemplatesPage";
import ScheduledMessagesPage from "../components/sales/pages/scheduled-messages/ScheduledMessagesPage";

// ✅ Marketing pages
import MarketingDashboardPage from "../components/sales/pages/marketing-dashboard/MarketingDashboardPage";
import MarketingCampaignsPage from "../components/sales/pages/marketing-campaigns/MarketingCampaignsPage";

import { storage } from "../services/storage";
import { authService } from "../services/auth.service";
import { adminService } from "../services/admin.service";
import { productsService } from "../services/products.service";

const API_URL = "http://localhost:5000/api";

function RequireRole({ authData, allowed, children }) {
  if (!authData) return <Navigate to="/login" replace />;
  if (!allowed.includes(authData.role)) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect({ authData }) {
  if (!authData) return <Navigate to="/login" replace />;

  if (authData.role === "Admin") return <Navigate to="/control-panel" replace />;

  if (authData.role === "Sales") {
    if (authData.needsPendingApproval) return <Navigate to="/pending" replace />;
    return <Navigate to="/sales/clients" replace />;
  }

  if (authData.role === "Customer") {
    if (authData.needsPendingApproval) return <Navigate to="/pending" replace />;
    return <Navigate to="/products-dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
}

export default function AuthSystem() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [authData, setAuthData] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [products, setProducts] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductSelection, setShowProductSelection] = useState(false);

  const [viewingCompany, setViewingCompany] = useState(null);
  const [showProductManagement, setShowProductManagement] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });

  const [signupForm, setSignupForm] = useState({
    managerName: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    password: "",
    companyNumber: "",
    companyAddress: "",
  });

  const [files, setFiles] = useState({
    commercialRegistration: null,
    taxNumber: null,
    nationalAddress: null,
    logo: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  const getToken = () => {
    // مرن عشان اختلاف اسم التخزين
    try {
      const stored = storage.getAuthData?.();
      if (stored?.token) return stored.token;
      if (stored?.accessToken) return stored.accessToken;
    } catch {}
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      ""
    );
  };

  const toIsoOrNull = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    // نخليها UTC midnight لتفادي مشاكل الـ timezone
    return `${yyyyMmDd}T00:00:00.000Z`;
  };

  const assignProductToCompany = async (payload) => {
    // لو عندك method جاهزة في productsService استخدمها
    if (typeof productsService?.assign === "function") {
      return productsService.assign(payload);
    }
    

    // fallback مباشر على Swagger: POST /api/Products/assign (application/json)
    const token = getToken();
    const res = await fetch(`${API_URL}/Products/assign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `ASSIGN_FAILED (${res.status})`);
    }
    return true;
  };

  // Init auth from storage
  useEffect(() => {
    const stored = storage.getAuthData();
    if (stored) setAuthData(stored);
    setAuthInitialized(true);
  }, []);

  // Load products (public)
  useEffect(() => {
    (async () => {
      try {
        const data = await productsService.listPublic();
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        // ignore
      }
    })();
  }, []);

  // Load admin data when Admin logged in
  useEffect(() => {
    if (!authData || authData.role !== "Admin") return;

    const loadAdminData = async () => {
      setLoading(true);
      try {
        const [companiesData, statsData] = await Promise.all([
          adminService.getCompanies(),     // GET /api/Admin/companies
          adminService.getStatistics(),    // GET /api/Admin/statistics
        ]);

        setCompanies(Array.isArray(companiesData) ? companiesData : []);
        setStatistics(statsData ?? null);
      } catch (e) {
        if (e?.message === "UNAUTHORIZED") {
          showMessage("error", t("messages.sessionExpired"));
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    loadAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const data = await authService.login(loginForm);
      setAuthData(data);
      // سيبي HomeRedirect يقرر الوجهة
      navigate("/", { replace: true });
    } catch (e) {
      console.log("[LOGIN] error:", e);
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const buildRegisterFormData = () => {
    const fd = new FormData();
    fd.append("ManagerName", signupForm.managerName);
    fd.append("CompanyName", signupForm.companyName);
    fd.append("Email", signupForm.email);
    fd.append("PhoneNumber", signupForm.phoneNumber);
    fd.append("Password", signupForm.password);
    fd.append("CompanyNumber", signupForm.companyNumber);
    if (signupForm.companyAddress) fd.append("CompanyAddress", signupForm.companyAddress);

    selectedProducts.forEach((id) => fd.append("SelectedProductIds", id));

    Object.entries(files).forEach(([k, v]) => {
      if (v) fd.append(k, v);
    });

    return fd;
  };

const handleSignup = async () => {
  if (!signupForm.managerName || !signupForm.email || !signupForm.password) {
    showMessage("error", t("validation.requiredSignupFields"));
    return;
  }

  setLoading(true);
  try {
    const fd = buildRegisterFormData();
    const data = await authService.register(fd);

    // ✅ مهم جدًا عشان /pending ما يرجعش /login
    const stored = storage.getAuthData();
    if (stored) setAuthData(stored);

    showMessage("success", t("messages.signupSuccess"));
    setTimeout(() => {
      navigate("/pending", {
        state: {
          companyName: signupForm.companyName,
          companyNumber: data?.companyNumber || data?.company?.companyNumber || "",
        },
      });
    }, 1500);
  } catch (e) {
    showMessage("error", e?.message || t("messages.networkError"));
  } finally {
    setLoading(false);
  }
};

  // ✅ FIX: يرجّع boolean للـ modal + يحدّث المنتجات على /api/Products/assign
  const handleUpdateCompany = async (companyId, editData) => {
    try {
      // 1) Update company payment status: PUT /api/Admin/companies/{id}/payment-status
      await adminService.updatePaymentStatus(companyId, !!editData?.hasPaid);

      // 2) Update per-product subscription if present
      const access = editData?.productAccess || {};
      const entries = Object.entries(access);

      if (entries.length > 0) {
        await Promise.all(
          entries.map(([productIdStr, v]) => {
            const productId = Number(productIdStr);
            const hasAccess = !!v?.hasAccess;

            return assignProductToCompany({
              companyId: Number(companyId),
              productId: Number(productId),
              hasPaid: !!v?.hasPaid,
              hasAccess,
              licenseStartDate: hasAccess ? toIsoOrNull(v?.startDate) : null,
              licenseEndDate: hasAccess ? toIsoOrNull(v?.endDate) : null,
            });
          })
        );
      }

      // 3) Refresh admin dashboard data
      const [companiesData, statsData] = await Promise.all([
        adminService.getCompanies(),
        adminService.getStatistics(),
      ]);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setStatistics(statsData ?? null);

      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        showMessage("error", t("messages.sessionExpired"));
        logout();
        return false;
      }

      alert(
        t("messages.updateCompanyErrorWithReason", {
          reason: e?.message || "Error",
        })
      );
      return false;
    }
  };

  const logout = () => {
    storage.clear();
    setAuthData(null);
    setLoginForm({ email: "", password: "" });
    navigate("/login", { replace: true });
  };

  const handleOpenProduct = (product) => {
    // fallback للداشبورد بتاع الـ Customer
    const url = product?.productUrl || product?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!authInitialized) return null;

  return (
    <div>
      <Routes>
        {/* Home */}
        <Route path="/" element={<HomeRedirect authData={authData} />} />

        {/* Auth */}
        <Route
          path="/login"
          element={
            authData ? (
              <HomeRedirect authData={authData} />
            ) : (
              <LoginView
                loginForm={loginForm}
                setLoginForm={setLoginForm}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                handleLogin={handleLogin}
                loading={loading}
                message={message}
              />
            )
          }
        />

        <Route
          path="/signup"
          element={
            <SignupView
              signupForm={signupForm}
              setSignupForm={setSignupForm}
              handleSignup={handleSignup}
              loading={loading}
              message={message}
              products={products}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              files={files}
              setFiles={setFiles}
              showProductSelection={showProductSelection}
              setShowProductSelection={setShowProductSelection}
            />
          }
        />

        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />

        {/* Pending */}
        <Route
          path="/pending"
          element={
            <RequireRole authData={authData} allowed={["Sales", "Customer", "Admin"]}>
              <PendingView authData={authData} onLogout={logout} />
            </RequireRole>
          }
        />

        {/* Customer Dashboard */}
        <Route
          path="/products-dashboard"
          element={
            <RequireRole authData={authData} allowed={["Customer"]}>
              <ProductsDashboard
                products={authData?.availableProducts || []}
                onSelectProduct={handleOpenProduct}   // ✅ FIX
                onLogout={logout}
              />
            </RequireRole>
          }
        />

        {/* Admin Dashboard */}
        <Route
          path="/control-panel"
          element={
            <RequireRole authData={authData} allowed={["Admin"]}>
              <ControlPanelView
                authData={authData}
                companies={companies}
                statistics={statistics}
                loading={loading}
                logout={logout}
                setViewingCompany={setViewingCompany}
                setShowProductManagement={setShowProductManagement}
              />
            </RequireRole>
          }
        />

        {/* ✅ Sales Area */}
        <Route
          path="/sales"
          element={
            <RequireRole authData={authData} allowed={["Sales", "Admin"]}>
              <SalesLayout authData={authData} logout={logout} />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="clients" replace />} />

          <Route path="clients" element={<ClientsPage authData={authData} logout={logout} />} />
          <Route
            path="client-interactions"
            element={<ClientInteractionsPage authData={authData} logout={logout} />}
          />
          <Route path="sales-notes" element={<SalesNotesPage authData={authData} logout={logout} />} />
          <Route path="sales-templates" element={<SalesTemplatesPage authData={authData} logout={logout} />} />
          <Route
            path="scheduled-messages"
            element={<ScheduledMessagesPage authData={authData} logout={logout} />}
          />

          {/* ✅ Marketing */}
          <Route
            path="marketing-dashboard"
            element={<MarketingDashboardPage authData={authData} logout={logout} />}
          />
          <Route
            path="marketing-campaigns"
            element={<MarketingCampaignsPage authData={authData} logout={logout} />}
          />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Modals */}
      {viewingCompany && (
        <CompanyDetailsModal
          company={viewingCompany}
          onClose={() => setViewingCompany(null)}
          onUpdate={handleUpdateCompany}   // ✅ now returns boolean
          allProducts={products}
          loading={loading}
        />
      )}

      {showProductManagement && (
        <ProductManagementModal
          onClose={() => setShowProductManagement(false)}
          onRefresh={async () => {
            try {
              const data = await productsService.listPublic();
              setProducts(Array.isArray(data) ? data : []);
            } catch {}
          }}
        />
      )}

      {showProductSelection && (
        <ProductSelectionModal
          products={products}
          onContinue={(selected) => {
            setSelectedProducts(selected);
            setShowProductSelection(false);
          }}
          onClose={() => setShowProductSelection(false)}
        />
      )}
    </div>
  );
}
